import React, {useEffect, useMemo, useState} from 'react';

import {Subscription} from 'src/components/subscription/subscription';
import {getChangeRequestInfo} from 'src/modules/event-create/services';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {
    getSubscriptionsByChangeRequestId,
    getSubscriptionsByChangeRequestIdSuccess,
    removeChangeRequestSubscriptionById,
    removeChangeRequestSubscriptionByIdSuccess,
    saveChangeRequestSubscription,
    saveChangeRequestSubscriptionSuccess,
} from 'src/openapi/apis/ChangeRequestSubscriptionControllerApi';
import {ChangeRequestSubscription} from 'src/openapi/models/ChangeRequestSubscription';
import {useAppDispatch, useAppSelector} from 'src/store';

import {LocalStorage} from '@pb/kit-util-client-storage';

type Props = {
    changeRequestId: number | null,
};

const SUBSCRIPTION_TITLE = 'WATCHING THIS REQUEST';

export const ChangeRequestSubscriptionDropdown = ({changeRequestId}: Props) => {
    const dispatch = useAppDispatch();
    const userEmail = LocalStorage.get('user')?.email || '';
    const changeRequest = useAppSelector(getChangeRequestInfo)?.[changeRequestId || 0];
    const changeRequestAuthor = changeRequest?.authorEmail;
    const [subscribed, setSubscribed] = useState(false);
    const [shownSubscription, setShownSubscription] = useState(false);
    const [shownAutoSuggest, setShownAutoSuggest] = useState(false);
    const [subscriptions, setSubscriptions] = useState<Array<ChangeRequestSubscription>>([]);

    const watchersEmails = useMemo(() => {
        return [...new Set(subscriptions.map(subscription => subscription.userEmail))];
    }, [subscriptions]);

    useEffect(() => {
        if (changeRequestId) {
            dispatch(getSubscriptionsByChangeRequestId({changeRequestId})).then(e => {
                if (!e.payload.error && e.type === getSubscriptionsByChangeRequestIdSuccess.type) {
                    setSubscriptions(e.payload.data);
                }
            });
        }
    }, [changeRequestId, dispatch, changeRequestAuthor]);

    useEffect(() => {
        setSubscribed(watchersEmails.includes(userEmail));
    }, [userEmail, watchersEmails]);

    const saveSubscription = (email: string) => {
        if (watchersEmails.includes(email)) {
            return;
        }

        if (changeRequestId) {
            const changeRequestSubscription: ChangeRequestSubscription = {
                changeRequestId,
                userEmail: email,
            };

            dispatch(saveChangeRequestSubscription({changeRequestSubscription})).then(e => {
                if (!e.payload.error && e.type === saveChangeRequestSubscriptionSuccess.type) {
                    const savedId = e.payload.data;
                    setSubscriptions(prevWatchers => [...prevWatchers,
                        {id: savedId, ...changeRequestSubscription},
                    ]);

                    if (email === userEmail) {
                        setSubscribed(true);
                    }
                }
            });
        }
    };

    const onWatcherRemove = (id: number) => {
        dispatch(removeChangeRequestSubscriptionById({id})).then(e => {
            if (!e.payload.error && e.type === removeChangeRequestSubscriptionByIdSuccess.type) {
                setSubscriptions(prevWatchers => prevWatchers.filter(item => item.id !== id));
                setShownAutoSuggest(false);
            }
        });
    };

    return (
        <>
            <UsersInfoController emails={watchersEmails}/>
            <Subscription
                title={SUBSCRIPTION_TITLE}
                subscribed={subscribed}
                disabled={!changeRequestId}
                shownAutoSuggest={shownAutoSuggest}
                shownSubscription={shownSubscription}
                userEmail={userEmail}
                watchers={subscriptions}
                watchersEmails={watchersEmails}
                onWatcherRemove={onWatcherRemove}
                setShown={setShownSubscription}
                setShownAutoSuggest={setShownAutoSuggest}
                setSubscribed={setSubscribed}
                saveSubscription={saveSubscription}
            />
        </>
    );
};
